import { usePeriodoGestacionalQuery } from 'graphql/hooks.generated'
import { AtendimentoProfissional } from 'graphql/types.generated'
import { useMemo } from 'react'
import { dateAsYyyyMmDd, toDate } from 'util/date/formatDate'
import { filtrarAtendimentosPeriodoGestacional } from 'view/atendimentos/detail/components/modals/medicoes/pre-natal/util-medicoesPreNatal'

import { ResultadosExamesModel } from '../../../objetivo/resultados-exames/model-resultadosExames'
import {
  filterResultadosExamesAndConvertToExamePreNatalInput,
  isResultadoExamePreNatal,
} from '../../../objetivo/resultados-exames/util-resultadosExames'
import { convertPeriodoGestacionalToIdadeGestacional } from '../converter-acompanhamentoPreNatal'
import {
  AcompanhamentoPreNatalQueryModel,
  IdadeGestacionalAcompanhamentoPreNatal,
  PeriodoGestacionalQueryModel,
} from '../model-acompanhamentoPreNatal'

interface UsePeriodoGestacionalProps {
  prontuarioId?: string
  dum: Date
  dumAtendimentoAtual?: Date
  dataAtendimento: Date | number
  resultadosExamesAtendimentoAtual?: ResultadosExamesModel
  skip?: boolean
  atendimentosProfUltimaGestacao?: (AcompanhamentoPreNatalQueryModel | AtendimentoProfissional)[]
}

export interface UsePeriodoGestacionalResult
  extends Omit<
    PeriodoGestacionalQueryModel,
    'idadeGestacional' | 'isEcografico' | 'dataInicioGestacao' | 'dataProvavelPartoEcografica'
  > {
  dataInicioGestacao: Date
  dataProvavelPartoEcografica?: Date
  idadeGestacional: IdadeGestacionalAcompanhamentoPreNatal
  loading: boolean
  atendimentosPeriodoGestacional: (AcompanhamentoPreNatalQueryModel | AtendimentoProfissional)[]
}

export function usePeriodoGestacional({
  prontuarioId,
  dum,
  dumAtendimentoAtual,
  dataAtendimento,
  resultadosExamesAtendimentoAtual,
  skip,
  atendimentosProfUltimaGestacao = [],
}: UsePeriodoGestacionalProps): UsePeriodoGestacionalResult {
  const resultadosExamesPreNatalAsInput =
    resultadosExamesAtendimentoAtual &&
    filterResultadosExamesAndConvertToExamePreNatalInput(resultadosExamesAtendimentoAtual, isResultadoExamePreNatal)

  const { data, loading } = usePeriodoGestacionalQuery({
    skip: !prontuarioId || !dum || !dataAtendimento || skip,
    variables: {
      input: {
        prontuarioId,
        dum,
        dumAtendimentoAtual,
        dataAtendimento: dateAsYyyyMmDd(dataAtendimento),
        resultadosExamesPreNatalAtendimentoAtual: resultadosExamesPreNatalAsInput,
      },
    },
  })

  const periodoGestacional = data?.periodoGestacional
  const dataInicioGestacao = toDate(periodoGestacional?.dataInicioGestacao)

  const atendimentosPeriodoGestacional = useMemo(() => {
    if (loading) return atendimentosProfUltimaGestacao

    return filtrarAtendimentosPeriodoGestacional(atendimentosProfUltimaGestacao, dataInicioGestacao)
  }, [loading, atendimentosProfUltimaGestacao, dataInicioGestacao])

  return {
    ...periodoGestacional,
    dataInicioGestacao,
    dataProvavelPartoEcografica: toDate(periodoGestacional?.dataProvavelPartoEcografica),
    idadeGestacional: convertPeriodoGestacionalToIdadeGestacional(periodoGestacional),
    loading,
    atendimentosPeriodoGestacional,
  }
}
